import CloseIcon from "@mui/icons-material/CloseOutlined";
import { Box, Drawer, IconButton, Stack, Typography } from "@mui/material";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import ScrollableFlexContainer from "@entrilia/shared/components/ScrollableFlexContainer";
import { logError } from "@entrilia/shared/logging";
import { useEnhancedBiApiClientProvider } from "../../../contexts/ApiClientProviderContext";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch";
import {
  selectVersionHistory,
  selectVersionHistoryError,
  selectVersionHistoryLoading,
} from "../../../store/selectors/versionHistorySelectors";
import { getVersionHistory } from "../../../store/thunks/versionHistoryStateThunks";
import useVersionHistory from "./useVersionHistory";
import VersionHistoryList from "./VersionHistoryList";

const DRAWER_WIDTH = 420;

const VersionHistoryPanel = () => {
  const { getReportHistory } = useEnhancedBiApiClientProvider();
  const dispatch = useAppDispatch();

  const versions = useSelector(selectVersionHistory);
  const isLoading = useSelector(selectVersionHistoryLoading);
  const error = useSelector(selectVersionHistoryError);

  useEffect(() => {
    dispatch(getVersionHistory(getReportHistory));
  }, [dispatch, getReportHistory]);

  if (error) {
    logError(error, "[VersionHistoryPanel] fetch");
  }

  const {
    currentConfiguration,
    onPreviewVersion,
    isReportChanged,
    selectedVersionId,
    currentVersionId,
    previewData,
    handleClose,
  } = useVersionHistory();

  if (currentConfiguration?.error) {
    logError(currentConfiguration?.error, "[VersionHistoryPanel] getCurrentConfigurations");
    return <DataLoadingFailed title="Failed to load version history" />;
  }

  return (
    <Drawer
      anchor="right"
      open={true}
      variant="persistent"
      sx={{
        width: DRAWER_WIDTH,
        flexShrink: 0,
        "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box", position: "relative" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 2,
          borderBottom: 1,
          borderColor: "divider",
        }}
      >
        <Typography variant="h6">Version History</Typography>
        <Stack direction="row">
          <IconButton onClick={handleClose} title="Close" disabled={!!previewData}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>
      {error ? (
        <DataLoadingFailed title="Failed to load version history" />
      ) : (
        <>
          {isLoading ? (
            <InlineLoader />
          ) : (
            <>
              {versions.length === 0 && (
                <Stack sx={{ alignItems: "center", justifyContent: "center", py: 4 }}>
                  <Typography variant="body1" color="textSecondary">
                    No versions yet
                  </Typography>
                </Stack>
              )}
              {versions.length > 0 && (
                <ScrollableFlexContainer sx={{ flex: 1, overflow: "hidden" }}>
                  <VersionHistoryList
                    versions={versions}
                    isReportChanged={isReportChanged}
                    currentVersionId={currentVersionId}
                    selectedVersionId={selectedVersionId}
                    previewData={previewData}
                    onPreviewVersion={onPreviewVersion}
                    currentConfiguration={currentConfiguration}
                  />
                </ScrollableFlexContainer>
              )}
            </>
          )}
        </>
      )}
    </Drawer>
  );
};

export default VersionHistoryPanel;
